import React, { useState, useEffect } from "react";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import * as OrderApi from "@src/api/OrderApi";
import { Link } from "react-router-dom";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import AddressDisplay from "@src/components/common/AddressDisplay/AddressDisplay";
import { useParams } from "react-router-dom";
import Tile from "@src/components/containers/Tile";
import get from "lodash/get";
import { SimpleTable, useToast } from "@swordsweeper/ssi-ui-component-library";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import { formatDateShort } from "@src/utils/formatDate";
import DistributorLogo from "@src/components/common/DistributorLogo";
import { distributorSlugToName } from "@src/constants/Distributors";
import OrderedDistributorProductDetails from "@src/components/widgets/OrderedDistributorProductDetails";
import { OrderedDistributorProduct } from "@src/types/product";
import classnames from "classnames";
import { getOrderStatusDisplay } from "@src/utils/order";

interface Address {
  [key: string]: any;
}

interface SavingsAndReward {
  distributorSlug: string;
  rewardTier: string;
  rewardDescription: string;
  [key: string]: any;
}

interface DistributorProductGrouping {
  distributorId: string | number;
  distributorSlug: string;
  [key: string]: any;
}

interface Order {
  deliverToAddress?: Address;
  savingsAndRewards?: SavingsAndReward[];
  distributorProductGroupings?: DistributorProductGrouping[];
  subtotal: number;
  [key: string]: any;
}

interface RouteParams extends Record<string, string | undefined> {
  orderNumber?: string;
}

function OrderPlacedPage(): React.JSX.Element {
  const { showToast } = useToast();
  const [isLoadingOrder, setIsLoadingOrder] = useState<boolean>(false);
  const [order, setOrder] = useState<Order | null>(null);
  const { orderNumber } = useParams<RouteParams>();

  const orderDistributorSlug = get(
    order,
    "distributorProductGroupings[0].distributorSlug",
    "",
  );
  const orderTrackingNumber = get(
    order,
    "distributorProductGroupings[0].trackingNumber",
    "",
  );
  const deliverToClinicName = get(order, "deliverToClinicName", "");
  const deliverToAddress = get(order, "deliverToAddress", {}) as Address;
  const savingsAndRewards = get(
    order,
    "savingsAndRewards",
    [],
  ) as SavingsAndReward[];
  const statusTimestamps = {
    placedAtTimestamp: get(order, "placedAtTimestamp", ""),
    shippedAtTimestamp: get(
      order,
      "distributorProductGroupings[0].shippedAtTimestamp",
      "",
    ),
    deliveredAtTimestamp: get(
      order,
      "distributorProductGroupings[0].deliveredAtTimestamp",
      "",
    ),
    canceledAtTimestamp: get(
      order,
      "distributorProductGroupings[0].canceledAtTimestamp",
      "",
    ),
    returnedAtTimestamp: get(
      order,
      "distributorProductGroupings[0].returnedAtTimestamp",
      "",
    ),
  };

  useEffect(() => {
    if (orderNumber) {
      handleLoadOrder();
    }
  }, [orderNumber]);

  const handleLoadOrder = async (): Promise<void> => {
    setIsLoadingOrder(true);
    try {
      const response = await OrderApi.getOrder(orderNumber!);
      setOrder(response.body);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading order",
        variant: "error",
      });
    } finally {
      setIsLoadingOrder(false);
    }
  };

  return (
    <div className="flex-row flex-center">
      <div className="width-50">
        {isLoadingOrder && <SpinningLoader />}
        {!!order && (
          <div className="spaced-content-v-xl">
            <div>
              <SectionHeader
                header={
                  <span
                    className="text-xl"
                    data-testid="order-placed-page-header"
                  >
                    My Order from {formatDateShort(order.placedAtTimestamp)}
                  </span>
                }
              />

              <div className="spaced-content-v-lg overflow-x-scroll">
                <div className="flex-row padding-bottom-sm">
                  <div
                    className="flex-1"
                    data-testid="order-placed-page-order-details-section"
                  >
                    <div className="text-lg font-weight-bold">
                      Order Details
                    </div>
                    <div className="margin-y-sm text-lg">
                      <div data-testid="order-placed-page-order-number">
                        Order #{orderNumber}
                      </div>
                      <div>
                        Total{" "}
                        <PriceDisplay
                          data-testid="order-placed-page-total-amount"
                          amount={order.subtotal}
                        />
                      </div>
                    </div>
                    <div className="spaced-content-v-sm">
                      <div
                        className="text-sm"
                        data-testid="order-placed-page-placed-by-user"
                      >
                        Placed by{" "}
                        {order.placedByUser?.fullName || "Unidentified User"}
                      </div>
                      <div>
                        <span
                          className="text-xs text-link link"
                          onClick={() => alert("TODO")}
                          data-testid="order-placed-page-download-invoice-link"
                        >
                          Download PDF Invoice
                        </span>
                      </div>
                      <div>
                        <span
                          className="text-xs text-link link"
                          onClick={() => alert("TODO")}
                        >
                          Contact Your{" "}
                          {distributorSlugToName(orderDistributorSlug)} Rep
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="flex-1"
                    data-testid="order-placed-page-delivering-to-section"
                  >
                    <div className="text-lg font-weight-bold">
                      Delivering to:
                    </div>
                    <div className="margin-y-sm text-lg">
                      <div>{deliverToClinicName || "Unidentified Clinic"}</div>
                      <AddressDisplay {...deliverToAddress} />
                    </div>
                    <div>
                      <span className="text-xs">
                        Track Your Order: &nbsp;
                        <span
                          className="text-link link"
                          onClick={() => alert("TODO")}
                        >
                          {orderTrackingNumber || "TBD"}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {savingsAndRewards.length > 0 && (
                  <Tile data-testid="order-placed-page-rewards-tile">
                    <div className="text-lg font-weight-bold">Rewards</div>
                    <div className="padding-y-sm">
                      Way to go! You're saving more with each order! Here's your
                      rewards summary after this order...
                    </div>

                    <div className="margin-bottom-xs border-bottom">
                      <SimpleTable
                        className="table-no-headers table-no-row-borders"
                        hideTopBorder
                        columns={[
                          {
                            key: "distributorSlug",
                            render: ({ distributorSlug }: SavingsAndReward) => (
                              <DistributorLogo
                                distributorSlug={distributorSlug}
                                height={24}
                              />
                            ),
                          },
                          {
                            key: "rewardTier",
                          },
                          {
                            key: "rewardDescription",
                          },
                        ]}
                        data={savingsAndRewards}
                      />
                    </div>
                    {/* @ts-ignore */}
                    <Link className="text-sm">View All Rewards</Link>
                  </Tile>
                )}

                {order.distributorProductGroupings?.map(
                  (distributorProductGrouping: any) => (
                    <React.Fragment
                      key={`distributor-product-grouping-${distributorProductGrouping.distributorId}`}
                    >
                      <Tile
                        data-testid={`order-placed-page-distributor-product-grouping-${distributorProductGrouping.distributorId}`}
                      >
                        <div className="flex-row flex-center space-between padding-bottom-md border-bottom ">
                          <DistributorLogo
                            distributorSlug={
                              distributorProductGrouping.distributorSlug
                            }
                            height={24}
                          />
                          <div
                            className="text-secondary text-sm flex-row flex-center spaced-content"
                            data-testid={`order-placed-page-distributor-product-grouping-${distributorProductGrouping.distributorId}-summary-header`}
                          >
                            <span>
                              {distributorProductGrouping.products.length} item
                              {distributorProductGrouping.products.length === 1
                                ? ""
                                : "s"}
                            </span>
                            <span>|</span>
                            <span>
                              Total: <PriceDisplay amount={order.subtotal} />
                            </span>
                          </div>

                          <div
                            className="text-secondary text-sm"
                            data-testid={`order-placed-page-distributor-product-grouping-${distributorProductGrouping.distributorId}-status`}
                          >
                            {getOrderStatusDisplay(
                              distributorProductGrouping.status,
                              statusTimestamps,
                            )}
                          </div>
                        </div>
                        {distributorProductGrouping.products.map(
                          (
                            distributorProduct: OrderedDistributorProduct,
                            index: number,
                          ) => (
                            <div
                              key={`dpg-${distributorProductGrouping.distributorId}-ordered-distributor-product-${distributorProduct.id}`}
                              data-testid={`dpg-${distributorProductGrouping.distributorId}-ordered-distributor-product-${distributorProduct.id}`}
                              className={classnames("padding-y-md", {
                                "border-bottom":
                                  index <
                                  distributorProductGrouping.products.length -
                                    1,
                              })}
                            >
                              <OrderedDistributorProductDetails
                                distributorProduct={distributorProduct}
                              />
                            </div>
                          ),
                        )}
                      </Tile>
                    </React.Fragment>
                  ),
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderPlacedPage;
